import { type FunctionComponent, useContext, useState } from 'react';
import { Content, PageLayout } from '@atlaskit/page-layout';
import { Root } from '@atlaskit/navigation-system/layout/root';
import { Main } from '@atlaskit/navigation-system/layout/main';
import { useFeatureGate } from '@atlassian/feature-gates-react';
import { Box } from '@atlaskit/primitives';
import { OAuthConsentFeatureGate } from '../FeatureGates/FeatureGates';
import Navigation, { Nav4 } from '../Navigation';
import Footer from '../Footer/Footer';
import MainContent from '../MainContent/MainContent';
import McpConsentLayout from '../MainContent/MCP-optional-sites/McpConsentLayout';
import TwgConsentLayout from '../MainContent/TWG/TwgConsentLayout';
import RovoConsentLayout from '../MainContent/Rovo/RovoConsentLayout';
import ConsentForm from './ConsentForm';
import {
  ReactConsentContext,
  narrowConsentContentStyle,
  useToken,
} from './ConsentContextProvider';

// Re-exported for back-compat with existing importers (e.g. VR fixtures).
export { narrowConsentContentStyle };

/**
 * Renders consent navigation, chrome and form. App mounts this underneath
 * FeatureGateProvider so the first gate value is initialized.
 * This shell owns the main landmark.
 *
 * MCP, TWG and Rovo own their form and footer (and MCP its context providers)
 * because their styling differs from the other consent flows. Every other
 * service type is dispatched by MainContent inside the shared chrome.
 */
const ConsentLayout: FunctionComponent = () => {
  const { serviceType } = useToken();
  const consentContext = useContext(ReactConsentContext);
  const nav4Enabled = useFeatureGate(OAuthConsentFeatureGate.NAV4_MIGRATION);
  // Keep navigation stable for this visit: live flag changes must not remount
  // consent forms and discard the user's selections.
  const [nav4ForVisit] = useState(nav4Enabled);

  let content;
  if (serviceType === 'MCP') {
    content = <McpConsentLayout />;
  } else if (serviceType === 'TWG') {
    content = <TwgConsentLayout />;
  } else if (serviceType === 'Rovo') {
    content = <RovoConsentLayout />;
  } else {
    content = (
      <Box xcss={narrowConsentContentStyle}>
        <ConsentForm
          action={serviceType === 'DAG' ? '/oauth/activate' : undefined}
        >
          <MainContent />
          <Footer acceptDisabled={consentContext?.acceptDisabled} />
        </ConsentForm>
      </Box>
    );
  }

  if (nav4ForVisit) {
    // Keep the skip targets, without Nav4's text-only caption outside landmarks.
    return (
      <Root skipLinksLabel="">
        <Nav4 />
        <Main>{content}</Main>
      </Root>
    );
  }

  // Nav3 rollback branch: remove with oauth-consent_nav4-migration at cleanup.
  return (
    <PageLayout>
      <Navigation />
      <Content>
        <main style={{ width: '100%', minWidth: 0 }}>{content}</main>
      </Content>
    </PageLayout>
  );
};

export default ConsentLayout;
